import type { AdvertisingConfig } from '../config/advertising.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { AdvertisingSlotDefinition } from '../level/segments/SegmentDefinition';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import type { AdProvider } from './AdProvider';
import type { AdCampaign, AdCreative } from './AdTypes';

/** A slot of a spawned segment with the creative assigned to it. */
export interface AdBinding {
  readonly id: string;
  readonly segment: SegmentInstance;
  readonly slot: AdvertisingSlotDefinition;
  readonly campaign: AdCampaign;
  readonly creative: AdCreative;
  visibleSeconds: number;
  maxScreenFraction: number;
  impressionCounted: boolean;
}

export type BindingListener = (binding: AdBinding) => void;

/**
 * Gameplay-independent ad logic: assigns creatives to the slots of spawned
 * segments and counts impressions from visibility samples. No collision,
 * no rendering – AdSurfacePresenter draws, this class decides and measures.
 */
export class AdSlotBinder {
  private readonly bindings = new Map<string, AdBinding>();
  private readonly bySegment = new Map<number, string[]>();
  private readonly boundListeners = new Set<BindingListener>();
  private readonly unboundListeners = new Set<BindingListener>();

  constructor(
    private readonly provider: AdProvider,
    private readonly config: AdvertisingConfig,
    private readonly events: EventBus<GameEvents>,
    private readonly runDistance: () => number,
    /** Further slots of a spawned segment – the ad surfaces of its decorations (TrackPresenter.decorationAdSlots). */
    private readonly extraSlots: (segment: SegmentInstance) => readonly AdvertisingSlotDefinition[] = () => [],
  ) {
    events.on('segment:spawned', ({ segment }) => this.bindSegment(segment));
    events.on('segment:recycled', ({ segment }) => this.unbindSegment(segment));
  }

  onBound(listener: BindingListener): void {
    this.boundListeners.add(listener);
  }

  onUnbound(listener: BindingListener): void {
    this.unboundListeners.add(listener);
  }

  all(): AdBinding[] {
    return [...this.bindings.values()];
  }

  /**
   * Interrupts every continuous-visibility window (pause, menus, game over):
   * impressions only count while a run is actually being played.
   */
  resetVisibility(): void {
    for (const binding of this.bindings.values()) {
      binding.visibleSeconds = 0;
      binding.maxScreenFraction = 0;
    }
  }

  /**
   * Visibility sample from the presenter. An impression counts once per
   * binding after `minVisibleTimeForImpression` of continuous visibility.
   */
  reportVisibility(bindingId: string, visible: boolean, screenFraction: number, dt: number): void {
    const binding = this.bindings.get(bindingId);
    if (!binding || binding.impressionCounted) return;
    const countsAsVisible = visible && screenFraction >= this.config.minScreenHeightFraction;
    if (!countsAsVisible) {
      binding.visibleSeconds = 0;
      binding.maxScreenFraction = 0;
      return;
    }
    binding.visibleSeconds += dt;
    binding.maxScreenFraction = Math.max(binding.maxScreenFraction, screenFraction);
    if (binding.visibleSeconds >= this.config.minVisibleTimeForImpression) {
      binding.impressionCounted = true;
      this.provider.recordImpression?.(binding.campaign.campaignId);
      this.events.emit('ad:impression', {
        impression: {
          slotInstanceId: binding.id,
          slotType: binding.slot.slotType,
          campaignId: binding.campaign.campaignId,
          companyId: binding.campaign.companyId,
          creativeId: binding.creative.creativeId,
          visibleSeconds: binding.visibleSeconds,
          maxScreenFraction: binding.maxScreenFraction,
          runDistance: this.runDistance(),
          timestamp: new Date().toISOString(),
        },
      });
    }
  }

  private bindSegment(segment: SegmentInstance): void {
    if (!this.config.enabled) return;
    const ids: string[] = [];
    for (const slot of [...segment.definition.advertisingSlots, ...this.extraSlots(segment)]) {
      const id = `${segment.uid}:${slot.id}`;
      // bare fence – not every slot carries a banner; in a sponsored game every booked surface does (ructec 2026-10-05)
      if (!this.provider.fillsEverySlot?.(slot.slotType) && !this.filled(id, slot.fillRate)) continue;
      const selection = this.provider.select(slot.slotType, { now: new Date(), runDistance: this.runDistance(), slotInstanceId: id });
      if (!selection) continue;
      const binding: AdBinding = {
        id,
        segment,
        slot,
        campaign: selection.campaign,
        creative: selection.creative,
        visibleSeconds: 0,
        maxScreenFraction: 0,
        impressionCounted: false,
      };
      this.bindings.set(id, binding);
      ids.push(id);
      for (const listener of this.boundListeners) listener(binding);
    }
    this.bySegment.set(segment.uid, ids);
  }

  /** Slot instances that carry a banner – for tests and the debug view. */
  isFilled(slotInstanceId: string, fillRate?: number): boolean {
    return this.config.enabled && this.filled(slotInstanceId, fillRate);
  }

  private filled(slotInstanceId: string, fillRate = this.config.slotFillRate): boolean {
    return unitHash(slotInstanceId) < fillRate;
  }

  private unbindSegment(segment: SegmentInstance): void {
    for (const id of this.bySegment.get(segment.uid) ?? []) {
      const binding = this.bindings.get(id);
      if (!binding) continue;
      this.bindings.delete(id);
      for (const listener of this.unboundListeners) listener(binding);
    }
    this.bySegment.delete(segment.uid);
  }
}

/** FNV-1a hash of a string mapped to [0, 1) – stable, no RNG stream consumed. */
function unitHash(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) / 0x100000000;
}
